import { ArrowLeftOutlined, ContactsOutlined } from '@ant-design/icons';
import { useCurrentUser } from '@framework/auth';
import {
  SubmissionResponsiveCard,
  TitleSubmissionRow,
} from '@framework/common-web';
import {
  DelegationMachine,
  IModuleDelegation,
  SubmissionStateTag,
  getModuleTitle,
  useSubmission,
  useSubmissionModules,
} from '@framework/frontoffice-submission';
import { t, useTranslation } from '@framework/i18n';
import {
  DividerTitleElement,
  FlexRow,
  LinkButton,
  ResponsiveCard,
  TableData,
  VerticalSpace,
  generatePath,
  useBreadcrumbItem,
  useHistory,
  useIdFromUrl,
} from '@framework/ui';
import { Button, Col, Row, Space } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import Title from 'antd/es/typography/Title';
import React, { useCallback, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { PageLayout } from '../../../ui/layout/PageLayout';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage';
import { ChangeSubmissionManagerButton } from './components/ChangeSubmissionManagerButton';
import { DelegateUserName } from './components/DelegateUserName';
import { EditModulesDelegationsDrawer } from './components/EditModulesDelegationsDrawer';
import { RemoveSubmissionAttorneyButton } from './components/RemoveSubmissionAttorneyButton';
import { delegationColumns } from './delegationColumns';
import { useDelegationData } from './hooks/useDelegationData';

function getSelectedModulesText(selectedRows: string[]) {
  if (selectedRows.length === 0) {
    return '';
  }
  if (selectedRows.length === 1) {
    return t(
      'selected-modules-text.un-modulo-selezionato',
      '1 modulo selezionato',
    );
  }
  return t(
    'selected-modules-text.moduli-selezionati',
    `{{selectedRows}} moduli selezionati`,
    { selectedRows: selectedRows.length },
  );
}

const StyledTitle = styled(Title)`
  &&& {
    font-size: ${({ theme }) => theme.fontSizeHeading3}px;
  }
`;

interface Params {
  id: string;
}

export interface IDelegationPageProps extends RouteComponentProps<Params> {}

export function DelegationPage(props: IDelegationPageProps) {
  const history = useHistory();
  const user = useCurrentUser();
  const { t } = useTranslation();
  const [submissionId] = useIdFromUrl(props, 'id');

  const {
    submission,
    title,
    inquiryRequest,
    isLoading: isSubmissionLoading,
  } = useSubmission(submissionId);
  const submissionModules = useSubmissionModules({ submissionId });

  const requiresFile = inquiryRequest.data?.necessitaDocumentoDelega ?? false;

  const { delegationData, isLoading, error } = useDelegationData({
    modules: submissionModules.modules,
    submissionId,
    applicant: submission?.datiTitolari,
  });

  const canEditAttorney = DelegationMachine.can(
    delegationData,
    '.editAttorney',
    user,
  );
  const canEditManager = DelegationMachine.can(
    delegationData,
    '.editManager',
    user,
  );
  const canEditDelegates = DelegationMachine.can(
    delegationData,
    '.editDelegates',
    user,
  );

  const openModulesDelegationsDrawer = useCallback(
    (selected: string[]) => {
      const data = delegationData.moduleDelegation.filter(delegation =>
        selected.includes(delegation.submissionModule.id ?? '<unknown>'),
      );

      setSelectedModules(data);
      setIsModulesDelegationsDrawerOpen(true);
    },
    [delegationData.moduleDelegation],
  );

  const buttonText = t(
    'delegation.page.delegation-table.button.gestisci',
    'Gestisci',
  );

  const tableColumns = useMemo(() => {
    const result = [...delegationColumns];
    if (canEditDelegates.allowed) {
      result.push({
        title: (
          <span className="sr-only">
            {t('delegation-page.azioni', 'Azioni')}
          </span>
        ),
        key: 'actions',
        align: 'right',
        render: (_, record) => (
          <Button
            onClick={() => {
              openModulesDelegationsDrawer([
                record.submissionModule.id ?? '<unknown>',
              ]);
            }}
          >
            {buttonText}
          </Button>
        ),
      });
    }
    return result;
  }, [canEditDelegates.allowed, openModulesDelegationsDrawer, buttonText, t]);

  useBreadcrumbItem({
    path: '/richieste',
    title: t('delegation.page.breadcrumb.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: generatePath('/richieste/:id', { id: submissionId ?? '<unknown>' }),
    title: title,
  });
  useBreadcrumbItem({
    path: history.location.pathname,
    title: t('delegation.page.breadcrumb.gestione-deleghe', 'Gestione Deleghe'),
  });

  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const [isModulesDelegationsDrawerOpen, setIsModulesDelegationsDrawerOpen] =
    useState(false);
  const [selectedModules, setSelectedModules] = useState<IModuleDelegation[]>(
    [],
  );

  // accessibilità per la selezione in tabella
  const rowSelection: TableRowSelection<IModuleDelegation> = {
    type: 'checkbox',
    selectedRowKeys: selectedRows,
    onChange: selected => setSelectedRows(selected as string[]),
    columnTitle: checkboxNode => {
      if (React.isValidElement(checkboxNode)) {
        return React.cloneElement(checkboxNode, {
          'aria-label': t(
            'delegation.page.delegation-table.checkbox.seleziona-tutti-i-moduli',
            'Seleziona tutti i moduli',
          ),
        } as any);
      }
      return checkboxNode;
    },
    getCheckboxProps: record => ({
      'aria-label':
        t(
          'delegation.page.delegation-table.checkbox.seleziona-modulo',
          'Seleziona modulo',
        ) +
        ' ' +
        getModuleTitle(record.submissionModule),
      disabled: undefined,
    }),
  };

  if (error) {
    if (error.response?.status === 403) {
      history.push('/richieste');
    }

    return (
      <NetworkAlertPage
        message={t(
          'delegation.page.error.impossibile-caricare-le-deleghe-della-richiesta',
          'Impossibile caricare le deleghe della richiesta',
        )}
        error={error}
      />
    );
  }

  console.log({ submission, submissionModules });

  return (
    <PageLayout>
      <SubmissionResponsiveCard
        loading={
          isLoading || submissionModules.isLoading || isSubmissionLoading
        }
        title={
          <TitleSubmissionRow
            stateTag={<SubmissionStateTag submission={submission} />}
          >
            {title}
          </TitleSubmissionRow>
        }
        extra={
          <LinkButton
            icon={<ArrowLeftOutlined aria-hidden />}
            size="large"
            to={generatePath('/richieste/:id/moduli/:moduleId', {
              id: submissionId ?? '<unknown>',
              moduleId: submissionModules.modules[0]?.id ?? '<unknown>',
            })}
            disabled={!submissionId || !submissionModules.modules.length}
            // onClick={() => history.goBack()}
          >
            {t('delegation-page.torna-alla-richiesta', 'Torna alla richiesta')}
          </LinkButton>
        }
        // cover={<div />}
      >
        <VerticalSpace />

        <FlexRow
          align="flex-start"
          right={
            process.env.BUILD_FLAGS_ADDRESS_BOOK && (
              <LinkButton
                icon={<ContactsOutlined aria-hidden />}
                type="primary"
                to={generatePath('/rubrica/richieste/:id/deleghe', {
                  id: submissionId ?? '<unknown>',
                })}
              >
                {t(
                  'delegation-page.address-book.button-label',
                  'Rubrica Delegati',
                )}
              </LinkButton>
            )
          }
        >
          <StyledTitle level={2}>
            {t('delegation-page.title', 'Gestione Deleghe')}
          </StyledTitle>{' '}
        </FlexRow>

        <div>
          <ResponsiveCard bordered size="small">
            {/* <Row style={{ display: 'flex', justifyContent: 'space-between' }}> */}
            <Row gutter={24}>
              <Col sm={24} md={12}>
                <div>
                  <div>{t('delegation-page.attorney', 'Procuratore')}</div>
                  <div>
                    {delegationData.attorney && (
                      <DelegateUserName name={delegationData.attorney?.name} />
                    )}
                    {canEditAttorney.allowed && (
                      <>
                        <ChangeSubmissionManagerButton
                          submissionId={submissionId}
                          iconOnly={delegationData.attorney != null}
                          type="attorney"
                          data={delegationData.attorney}
                          requiresFile={requiresFile}
                        />
                        {delegationData.attorney && (
                          <RemoveSubmissionAttorneyButton
                            submissionId={submissionId}
                            iconOnly
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Col>
              <Col sm={24} md={12}>
                <div>
                  <div>{t('delegation-page.manager', 'Gestore')}</div>
                  <div>
                    <DelegateUserName name={delegationData.manager?.name} />
                    {canEditManager.allowed && (
                      <ChangeSubmissionManagerButton
                        submissionId={submissionId}
                        iconOnly
                        type="manager"
                        data={delegationData.manager}
                      />
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </ResponsiveCard>
        </div>

        <VerticalSpace size={12} />

        <FlexRow
          right={
            canEditDelegates.allowed && (
              <Space>
                <div style={{ marginLeft: '12px' }}>
                  {getSelectedModulesText(selectedRows)}
                </div>
                <Button
                  type="default"
                  disabled={selectedRows.length === 0}
                  onClick={() => {
                    openModulesDelegationsDrawer(selectedRows);
                    setSelectedRows([]);
                  }}
                >
                  {t(
                    'delegation.page.button.gestisci-selezionati',
                    'Gestisci Selezionati',
                  )}
                </Button>
              </Space>
            )
          }
        >
          <DividerTitleElement
            title={t(
              'delegation.page.divider-title.deleghe-dei-moduli',
              'Deleghe dei Moduli',
            )}
          />
        </FlexRow>

        <TableData
          loading={isLoading}
          size="middle"
          columns={tableColumns}
          dataSource={delegationData.moduleDelegation}
          rowKey={record => record.submissionModule.id ?? '<unknown>'}
          rowSelection={canEditDelegates.allowed ? rowSelection : undefined}
          pagination={false}
        />
      </SubmissionResponsiveCard>
      <EditModulesDelegationsDrawer
        submissionId={submissionId ?? '<unknown>'}
        modulesDelegations={selectedModules}
        allDelegations={delegationData.moduleDelegation}
        open={isModulesDelegationsDrawerOpen}
        setOpen={setIsModulesDelegationsDrawerOpen}
      />
    </PageLayout>
  );
}
